import React from 'react';
import { motion } from 'framer-motion';
import { Play, Pause } from 'lucide-react';

/**
 * VideoPlayPauseButton
 * ---------------------------------------------------------------------------
 * Shared play/pause overlay used by every hero/video across the site
 * (HeroMedia, MediaUploadHero, StandardPageLayout hero video, TripleVideoHero).
 *
 * Behavior:
 *  - Absolutely positioned at the exact center of the parent video frame.
 *  - Hidden by default; only appears when the parent container is hovered
 *    (parent must add the Tailwind `group` class). Hides on mouse leave.
 *  - Same visual styling as the previous bottom-right play/pause button
 *    (40x40 black glass pill, Lucide Play/Pause icon).
 *
 * Note: this only styles the play/pause control. The mute/volume button
 * remains in its existing bottom-right position, untouched.
 */
const VideoPlayPauseButton = ({
  isPlaying,
  onClick,
  testId,
  motionDelay = 0.5,
}) => {
  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none opacity-0 group-hover/video:opacity-100 transition-opacity duration-200"
      aria-hidden={false}
    >
      <motion.button
        type="button"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: motionDelay }}
        onClick={onClick}
        className="pointer-events-auto w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm hover:bg-black/70 flex items-center justify-center transition-all duration-200 group/btn"
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
        data-testid={testId}
      >
        {isPlaying ? (
          <Pause className="w-5 h-5 text-white group-hover/btn:scale-110 transition-transform" />
        ) : (
          <Play className="w-5 h-5 text-white group-hover/btn:scale-110 transition-transform" />
        )}
      </motion.button>
    </div>
  );
};

export default VideoPlayPauseButton;
